import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { mergeResolutionSettings } from "../api/resolutions.js";
import Page from "../components/layout/Page.jsx";
import { Alert, Button, Field, Input, Select } from "../components/ui.jsx";
import { useOccurrence } from "../context/OccurrenceContext.jsx";
import { requestParentScroll, useIframeHeight } from "../hooks/useIframeHeight.js";
import { validatePixRefund } from "../utils/pixRefund.js";
import styles from "./Refund.module.css";

const KEY_TYPES = [
  { value: "cpf", label: "CPF" },
  { value: "cnpj", label: "CNPJ" },
  { value: "phone", label: "Celular" },
  { value: "email", label: "E-mail" },
  { value: "random", label: "Chave aleatória" },
];

const Refund = () => {
  const { shop, name, email } = useParams();
  const { occurrence, setOccurrence, resolutionSettings } = useOccurrence();
  const method = mergeResolutionSettings(resolutionSettings).refund.method;
  const [refundData, setRefundData] = useState({});
  const [accepted, setAccepted] = useState(false);
  const [message, setMessage] = useState("");
  const navigate = useNavigate();

  useIframeHeight();

  const handleRefund = (key, value) => {
    setRefundData((prev) => ({ ...prev, [key]: value }));
  };

  const handleClick = () => {
    if (!accepted) {
      setMessage("Você precisa concordar com os termos para continuar.");
      return;
    }
    // "both": a loja decide depois entre estorno e PIX, então pedimos a chave por garantia.
    // No modo "both" a chave PIX é opcional: só valida se o cliente começou a preencher.
    const pixFilled = Object.values(refundData || {}).some((value) => String(value || "").trim());
    if (method === "pix" || (method === "both" && pixFilled)) {
      const result = validatePixRefund(refundData);
      if (!result.ok) {
        setMessage(result.message);
        return;
      }
      setOccurrence({
        ...occurrence,
        paymentOrder: method === "pix" ? "pix" : undefined,
        refund: result.refund,
      });
    } else {
      setOccurrence({
        ...occurrence,
        paymentOrder: undefined,
        refund: undefined,
      });
    }
    requestParentScroll();
    navigate(`/${shop}/post/${name}${email ? `/${encodeURIComponent(email)}` : ""}`);
  };

  return (
    <Page
      title="Dados do estorno"
      subtitle={
        method === "pix"
          ? "Informe os dados da chave PIX. O lojista transfere depois da avaliação."
          : method === "both"
            ? "O valor volta no meio de pagamento da compra. Se quiser, informe uma chave PIX (opcional) caso a loja precise usar."
            : "O valor volta no mesmo meio de pagamento da compra, depois da avaliação."
      }
      step="review"
      onBack={() => navigate(-1)}
    >
      <div className={styles.stack}>
        {method === "shopify" ? (
          <Alert tone="info">
            Depois da avaliação do produto, o valor volta no mesmo meio de pagamento da compra.
            Você não precisa informar dados bancários.
          </Alert>
        ) : (
          <>
            <Field id="holder" label="Titular">
              <Input id="holder" value={refundData.holder || ""} onChange={(event) => handleRefund("holder", event.target.value)} />
            </Field>
            <Field id="cpf" label="CPF ou CNPJ">
              <Input id="cpf" value={refundData.cpf || ""} onChange={(event) => handleRefund("cpf", event.target.value)} />
            </Field>
            <Field id="keyType" label="Tipo de chave">
              <Select id="keyType" value={refundData.keyType || ""} onChange={(event) => handleRefund("keyType", event.target.value)}>
                <option value="">Selecione</option>
                {KEY_TYPES.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </Select>
            </Field>
            <Field id="pixKey" label="Chave">
              <Input id="pixKey" value={refundData.pixKey || ""} onChange={(event) => handleRefund("pixKey", event.target.value)} />
            </Field>
          </>
        )}

        <label className={styles.terms}>
          <input type="checkbox" checked={accepted} onChange={(event) => setAccepted(event.target.checked)} />
          Li e concordo com os <a href="https://fullcomm.io/privacidade/" target="_blank" rel="noreferrer">termos</a>.
        </label>
        {message ? <Alert>{message}</Alert> : null}
        <Button onClick={handleClick}>Continuar para revisão</Button>
      </div>
    </Page>
  );
};

export default Refund;
